import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Card } from '@/components/ui/card';
import { getBrowserIconComponent } from '@/components/metrics/browsers/BrowserIcons';

interface DataItem {
  name: string;
  value: number;
  percentage: number;
}

interface PremiumDonutChartProps {
  data: DataItem[];
  title: string;
  renderLegends?: boolean;
  customLegendComponent?: React.ReactNode;
  showBrowserIcons?: boolean;
}

// Paleta premium harmonizada com azul institucional Host VSL
const DONUT_COLORS = [
  '#3B82F6', // Maior valor - Azul Host institucional
  '#93C5FD', // Segundo - Azul suave
  '#BFDBFE', // Terceiro - Azul claro
  '#E5EDFF', // Quarto - Azul muito suave
  '#F1F5F9', // Demais - Cinza neutro
  '#F1F5F9'  // Demais - Cinza neutro
];

// Custom tooltip premium
const CustomTooltip = ({ active, payload }: any) => {
  useLanguage();
  if (active && payload && payload.length) {
    return (
      <div className="bg-white px-4 py-3 border border-[#E2E8FF] rounded-[12px] shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-in fade-in duration-150">
        <p className="text-[14px] font-semibold text-[#0A1A2F] mb-1">
          {payload[0].name}
        </p>
        <p className="text-[13px] text-[#6B7280]">
          {payload[0].value.toLocaleString(getInterfaceLanguage())} {" "}{translateInterfaceText("visualizações")}</p>
        <p className="text-[12px] font-medium text-[#3B82F6] mt-1">
          {payload[0].payload.percentage}{translateInterfaceText("% do total")}</p>
      </div>
    );
  }
  return null;
};

// Componente de label central do donut
interface CenterLabelProps {
  viewBox?: {
    cx?: number;
    cy?: number;
    innerRadius?: number;
    outerRadius?: number;
  };
  data: DataItem[];
  showBrowserIcons?: boolean;
}

const CenterLabel = ({ viewBox, data, showBrowserIcons }: CenterLabelProps) => {
  useLanguage();
  // Guard clause: verificar se viewBox existe e tem as propriedades necessárias
  if (!viewBox || typeof viewBox.cx === 'undefined' || typeof viewBox.cy === 'undefined') {
    return null;
  }
  
  // Verificar se há dados
  if (!data || data.length === 0) {
    return null;
  }
  
  const { cx, cy } = viewBox;
  const topItem = data[0]; // Item com maior valor
  
  return (
    <g>
      <text
        x={cx}
        y={cy - 8}
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-[#0A1A2F]"
        style={{ fontSize: '18px', fontWeight: 700 }}
      >
        {topItem.percentage}%
      </text>
      <text
        x={cx}
        y={cy + 12}
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-[#6B7280]"
        style={{ fontSize: '13px', fontWeight: 400 }}
      >
        {topItem.name}
      </text>
    </g>
  );
};

// Hook for responsive chart size
function useChartSize() {
  useLanguage();
  const [size, setSize] = React.useState(260);
  
  React.useEffect(() => {
    const updateSize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setSize(200); // Mobile - maior para melhor visualização
      } else if (width < 1024) {
        setSize(200); // Tablet
      } else {
        setSize(260); // Desktop - reduzido
      }
    };
    
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  
  return size;
}

export function PremiumDonutChart({ 
  data, 
  title,
  renderLegends = true,
  customLegendComponent,
  showBrowserIcons = false
}: PremiumDonutChartProps) {
  useLanguage();
  const chartSize = useChartSize();
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  
  // Verificação de dados vazios ou inválidos
  if (!data || data.length === 0) {
    return (
      <Card className="bg-white border border-[#E2E8FF] rounded-[14px] p-4 md:p-[18px] md:min-h-[260px] flex flex-col items-center justify-center">
        <p className="text-sm text-muted-foreground">{translateInterfaceText("Sem dados para exibir")}</p>
      </Card>
    );
  }
  
  return (
    <Card className="bg-white border border-[#E2E8FF] rounded-[14px] p-4 md:p-[18px] md:min-h-[260px] flex flex-col animate-in fade-in duration-450">
      {/* Título */}
      <h3 className="text-[15px] font-semibold text-[#0A1A2F] text-center mb-3">
        {title}
      </h3>

      {/* Gráfico Donut */}
      <div className="flex-1 flex items-center justify-center">
        <ResponsiveContainer width="100%" height={chartSize}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={<CenterLabel data={data} showBrowserIcons={showBrowserIcons} />}
              outerRadius={chartSize * 0.42}
              innerRadius={chartSize * 0.26}
              dataKey="value"
              nameKey="name"
              paddingAngle={2}
              animationBegin={0}
              animationDuration={450}
              animationEasing="ease-out"
              onMouseEnter={(_, index) => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
            {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={DONUT_COLORS[index]}
                  stroke="#FFFFFF"
                  strokeWidth={2}
                  strokeOpacity={0.8}
                  style={{ 
                    opacity: 1,
                    transform: hoveredIndex === index ? 'scale(1.04)' : 'scale(1)',
                    transformOrigin: 'center',
                    transition: 'transform 0.2s ease-out, opacity 0.2s ease-out'
                  }}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Browser icons legend (if enabled) */}
      {showBrowserIcons && (
        <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
          {data.map((item, index) => (
            <div 
              key={index} 
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-colors cursor-default"
            >
              {getBrowserIconComponent(item.name, 20)}
              <span className="text-[12px] font-medium text-[#4A4A4A]">{item.percentage}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Legendas customizadas (se fornecidas) */}
      {renderLegends && customLegendComponent && (
        <div className="mt-6">
          {customLegendComponent}
        </div>
      )}
    </Card>
  );
}

// Componente de legenda premium (para usar com o gráfico)
interface LegendItemProps {
  name: string;
  value: number;
  percentage: number;
  color: string;
}

export function DonutLegendItem({ name, value, percentage, color }: LegendItemProps) {
  useLanguage();
  return (
    <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-[12px] h-[58px] px-[16px] py-[14px] flex items-center justify-between hover:bg-[#F1F5F9] transition-colors duration-200">
      <div className="flex items-center gap-3">
        <div 
          className="w-3 h-3 rounded-full flex-shrink-0" 
          style={{ backgroundColor: color }}
        />
        <div>
          <p className="text-[14px] font-semibold text-[#2E2E2E] leading-tight">
            {name}
          </p>
          <p className="text-[12px] text-[#8A8A8A]">
            {percentage}{translateInterfaceText("% do total")}</p>
        </div>
      </div>
      <p className="text-[15px] font-semibold text-[#2E2E2E]">
        {value.toLocaleString(getInterfaceLanguage())}
      </p>
    </div>
  );
}

// Grid de legendas responsivo
interface DonutLegendsGridProps {
  data: DataItem[];
}

export function DonutLegendsGrid({ data }: DonutLegendsGridProps) {
  useLanguage();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
      {data.map((item, index) => (
        <DonutLegendItem
          key={index}
          name={item.name}
          value={item.value}
          percentage={item.percentage}
          color={DONUT_COLORS[index]}
        />
      ))}
    </div>
  );
}
